import type { DashboardsDocument, DashboardsWidgetConfig } from '@smarthome/api';
import {
  AlertCircle,
  CheckCircle,
  Upload,
  FileJson,
  FileCode,
  Eye,
  ChevronDown,
} from 'lucide-react';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import YAML from 'yaml';

import { getWidgetDefinition } from '../registry';
import type { EditorWidget, WidgetType } from '../types';
import { DashboardViewer } from '../viewer';

import { Button, Modal } from '@/components/ui';

export interface CodeEditorProps {
  document: { schemaVersion: number; widgets: EditorWidget[] };
  onApply: (document: { schemaVersion: number; widgets: EditorWidget[] }) => void;
}

type Format = 'json' | 'yaml';

interface ParsedDashboard {
  name?: string;
  description?: string;
  isShared?: boolean;
  document: DashboardsDocument;
}

interface ParseResult {
  type: 'single' | 'envelope';
  dashboards: ParsedDashboard[];
  warnings: string[];
}

function serializeDocument(
  doc: { schemaVersion: number; widgets: EditorWidget[] },
  format: Format
): string {
  // Clean up widgets to only include serializable properties
  const cleanDoc = {
    schemaVersion: doc.schemaVersion,
    widgets: doc.widgets.map((w) => ({
      id: w.id,
      type: w.type,
      title: w.title,
      x: w.x,
      y: w.y,
      w: w.w,
      h: w.h,
      config: w.config,
    })),
  };

  if (format === 'json') {
    return JSON.stringify(cleanDoc, null, 2);
  }
  return YAML.stringify(cleanDoc);
}

function validateWidget(widget: Record<string, unknown>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!widget['id'] || typeof widget['id'] !== 'string') {
    errors.push('Widget missing valid id');
  }
  if (!widget['type'] || typeof widget['type'] !== 'string') {
    errors.push('Widget missing valid type');
  } else {
    const def = getWidgetDefinition(widget['type'] as WidgetType);
    if (!def) {
      errors.push(`Unknown widget type: ${widget['type']}`);
    }
  }
  if (typeof widget['x'] !== 'number' || widget['x'] < 0) {
    errors.push('Widget missing valid x position');
  }
  if (typeof widget['y'] !== 'number' || widget['y'] < 0) {
    errors.push('Widget missing valid y position');
  }
  if (typeof widget['w'] !== 'number' || widget['w'] < 1) {
    errors.push('Widget missing valid width');
  }
  if (typeof widget['h'] !== 'number' || widget['h'] < 1) {
    errors.push('Widget missing valid height');
  }

  return { valid: errors.length === 0, errors };
}

function parseContent(content: string): ParseResult | { error: string } {
  const trimmed = content.trim();
  if (!trimmed) {
    return { error: 'Content is empty' };
  }

  let parsed: unknown;
  try {
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      parsed = JSON.parse(trimmed);
    } else {
      parsed = YAML.parse(trimmed);
    }
  } catch (err) {
    return { error: `Parse error: ${err instanceof Error ? err.message : 'Invalid content'}` };
  }

  if (!parsed || typeof parsed !== 'object') {
    return { error: 'Content must be a valid object' };
  }

  const warnings: string[] = [];
  const obj = parsed as Record<string, unknown>;

  // Check for export envelope (multi-dashboard)
  if (obj['exportSchemaVersion'] && Array.isArray(obj['dashboards'])) {
    const dashboards: ParsedDashboard[] = [];

    for (const d of obj['dashboards'] as Record<string, unknown>[]) {
      if (d['document'] && typeof d['document'] === 'object') {
        const doc = d['document'] as Record<string, unknown>;
        const widgets = Array.isArray(doc['widgets']) ? doc['widgets'] : [];

        // Validate widgets
        for (const w of widgets) {
          const validation = validateWidget(w as Record<string, unknown>);
          warnings.push(...validation.errors.map((e) => `Dashboard "${d['name']}": ${e}`));
        }

        dashboards.push({
          name: d['name'] as string | undefined,
          description: d['description'] as string | undefined,
          isShared: d['isShared'] as boolean | undefined,
          document: {
            schemaVersion: (doc['schemaVersion'] as number) ?? 1,
            widgets: widgets as DashboardsDocument['widgets'],
          },
        });
      }
    }

    return { type: 'envelope', dashboards, warnings };
  }

  // Check for single dashboard with document wrapper
  if (obj['document'] && typeof obj['document'] === 'object') {
    const doc = obj['document'] as Record<string, unknown>;
    const widgets = Array.isArray(doc['widgets']) ? doc['widgets'] : [];

    // Validate widgets
    for (const w of widgets) {
      const validation = validateWidget(w as Record<string, unknown>);
      warnings.push(...validation.errors);
    }

    return {
      type: 'single',
      dashboards: [
        {
          name: obj['name'] as string | undefined,
          description: obj['description'] as string | undefined,
          document: {
            schemaVersion: (doc['schemaVersion'] as number) ?? 1,
            widgets: widgets as DashboardsDocument['widgets'],
          },
        },
      ],
      warnings,
    };
  }

  // Check for raw document (just schemaVersion + widgets)
  if (obj['schemaVersion'] !== undefined || Array.isArray(obj['widgets'])) {
    const widgets = Array.isArray(obj['widgets']) ? obj['widgets'] : [];

    // Validate widgets
    for (const w of widgets) {
      const validation = validateWidget(w as Record<string, unknown>);
      warnings.push(...validation.errors);
    }

    return {
      type: 'single',
      dashboards: [
        {
          document: {
            schemaVersion: (obj['schemaVersion'] as number) ?? 1,
            widgets: widgets as DashboardsDocument['widgets'],
          },
        },
      ],
      warnings,
    };
  }

  return { error: 'Invalid format: expected dashboard document or export envelope' };
}

function documentToEditorWidgets(doc: DashboardsDocument): EditorWidget[] {
  return (doc.widgets ?? [])
    .map((w) => {
      if (!w.id || !w.type) return null;
      const def = getWidgetDefinition(w.type as WidgetType);
      if (!def) return null;

      return {
        id: w.id,
        type: w.type as WidgetType,
        title: w.title ?? def.displayName,
        x: w.x ?? 0,
        y: w.y ?? 0,
        w: w.w ?? def.defaultSize.w,
        h: w.h ?? def.defaultSize.h,
        config: (w.config ?? def.defaultConfig) as DashboardsWidgetConfig,
      };
    })
    .filter((w): w is EditorWidget => w !== null);
}

export function CodeEditor({ document, onApply }: CodeEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [format, setFormat] = useState<Format>('json');
  const [content, setContent] = useState(() => serializeDocument(document, 'json'));
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedDashboardIndex, setSelectedDashboardIndex] = useState(0);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  // Update content when format changes
  const handleFormatChange = useCallback(
    (newFormat: Format) => {
      try {
        // Try to parse current content and re-serialize in new format
        const result = parseContent(content);
        if ('error' in result) {
          // If current content is invalid, serialize from original document
          setContent(serializeDocument(document, newFormat));
        } else if (result.dashboards.length > 0) {
          // Re-serialize the first dashboard in new format
          const firstDashboard = result.dashboards[0];
          if (firstDashboard) {
            const doc = firstDashboard.document;
            const editorDoc = {
              schemaVersion: doc.schemaVersion ?? 1,
              widgets: documentToEditorWidgets(doc),
            };
            setContent(serializeDocument(editorDoc, newFormat));
          }
        }
      } catch {
        setContent(serializeDocument(document, newFormat));
      }
      setFormat(newFormat);
    },
    [content, document]
  );

  // Parse content on change
  useEffect(() => {
    if (!content.trim()) {
      setParseResult(null);
      setParseError(null);
      return;
    }

    const result = parseContent(content);
    if ('error' in result) {
      setParseError(result.error);
      setParseResult(null);
    } else {
      setParseError(null);
      setParseResult(result);
      setSelectedDashboardIndex(0);
    }
  }, [content]);

  // Handle file upload
  const handleFileSelect = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      if (typeof text === 'string') {
        setContent(text);
        // Auto-detect format
        const trimmed = text.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          setFormat('json');
        } else {
          setFormat('yaml');
        }
      }
    };
    reader.readAsText(file);
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      // Reset input so same file can be selected again
      e.target.value = '';
    },
    [handleFileSelect]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  // Apply to draft
  const handleApply = useCallback(() => {
    if (!parseResult || parseResult.dashboards.length === 0) return;

    const dashboard = parseResult.dashboards[selectedDashboardIndex];
    if (!dashboard) return;

    const editorWidgets = documentToEditorWidgets(dashboard.document);
    onApply({
      schemaVersion: dashboard.document.schemaVersion ?? 1,
      widgets: editorWidgets,
    });
  }, [parseResult, selectedDashboardIndex, onApply]);

  // Reset content
  const handleReset = useCallback(() => {
    setContent(serializeDocument(document, format));
  }, [document, format]);

  // Get preview document
  const previewDocument = useMemo(() => {
    if (!parseResult || parseResult.dashboards.length === 0) return null;
    const dashboard = parseResult.dashboards[selectedDashboardIndex];
    if (!dashboard) return null;
    return dashboard.document;
  }, [parseResult, selectedDashboardIndex]);

  const hasMultipleDashboards = (parseResult?.dashboards.length ?? 0) > 1;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-white/[0.06] dark:bg-[#18181b]">
        <div className="flex items-center gap-3">
          {/* Format selector */}
          <div className="flex rounded-lg border border-gray-200 dark:border-white/[0.08]">
            <button
              type="button"
              onClick={() => handleFormatChange('json')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                format === 'json'
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
                  : 'text-gray-600 hover:bg-gray-50 dark:text-zinc-400 dark:hover:bg-white/[0.04]'
              }`}
            >
              <FileJson className="h-4 w-4" />
              JSON
            </button>
            <button
              type="button"
              onClick={() => handleFormatChange('yaml')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                format === 'yaml'
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
                  : 'text-gray-600 hover:bg-gray-50 dark:text-zinc-400 dark:hover:bg-white/[0.04]'
              }`}
            >
              <FileCode className="h-4 w-4" />
              YAML
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".json,.yaml,.yml"
            onChange={handleFileChange}
            className="hidden"
          />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" />
            Upload File
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleReset}>
            Reset
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPreviewOpen(true)}
            disabled={!parseResult}
          >
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button size="sm" onClick={handleApply} disabled={!parseResult}>
            Apply to Draft
          </Button>
        </div>
      </div>

      {/* Multi-dashboard selector */}
      {hasMultipleDashboards && parseResult && (
        <div className="flex items-center gap-3 rounded-lg bg-amber-50 px-4 py-3 dark:bg-amber-500/10">
          <AlertCircle className="h-5 w-5 flex-shrink-0 text-amber-500" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
              Multiple dashboards found
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Select which dashboard to apply to the current draft
            </p>
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsSelectOpen(!isSelectOpen)}
              className="flex min-w-[200px] items-center justify-between rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm dark:border-amber-500/30 dark:bg-zinc-900"
            >
              <span className="truncate text-gray-900 dark:text-white">
                {parseResult.dashboards[selectedDashboardIndex]?.name ||
                  `Dashboard ${selectedDashboardIndex + 1}`}
              </span>
              <ChevronDown
                className={`ml-2 h-4 w-4 transition-transform ${isSelectOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isSelectOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setIsSelectOpen(false)} />
                <div className="absolute right-0 z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-white/[0.08] dark:bg-zinc-900">
                  {parseResult.dashboards.map((d, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => {
                        setSelectedDashboardIndex(i);
                        setIsSelectOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                        i === selectedDashboardIndex
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
                          : 'text-gray-700 hover:bg-gray-50 dark:text-zinc-300 dark:hover:bg-white/[0.04]'
                      }`}
                    >
                      {d.name || `Dashboard ${i + 1}`}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Editor area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`relative rounded-xl border transition-colors ${
          isDragging
            ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-500/5'
            : 'border-gray-200 dark:border-white/[0.06]'
        }`}
      >
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          spellCheck={false}
          className="h-96 w-full resize-none rounded-xl bg-gray-50 p-4 font-mono text-sm text-gray-900 focus:outline-none dark:bg-zinc-900 dark:text-white"
          placeholder={`Paste or type ${format.toUpperCase()} content here...`}
        />
        {isDragging && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-blue-50/80 dark:bg-blue-500/10">
            <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
              Drop file to load content
            </p>
          </div>
        )}
      </div>

      {/* Status */}
      {parseError && (
        <div className="flex items-start gap-3 rounded-lg bg-red-50 px-4 py-3 dark:bg-red-500/10">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
          <div>
            <p className="text-sm font-medium text-red-700 dark:text-red-300">Invalid content</p>
            <p className="mt-0.5 text-sm text-red-600 dark:text-red-400">{parseError}</p>
          </div>
        </div>
      )}

      {parseResult && (
        <div className="flex items-start gap-3 rounded-lg bg-green-50 px-4 py-3 dark:bg-green-500/10">
          <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-700 dark:text-green-300">Valid content</p>
            <p className="mt-0.5 text-sm text-green-600 dark:text-green-400">
              {parseResult.dashboards.length} dashboard(s),{' '}
              {parseResult.dashboards[selectedDashboardIndex]?.document.widgets?.length ?? 0}{' '}
              widgets
            </p>
            {parseResult.warnings.length > 0 && (
              <div className="mt-2 space-y-1">
                {parseResult.warnings.map((warning, i) => (
                  <p key={i} className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠ {warning}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <Modal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title="Preview"
        subtitle="Preview of parsed dashboard content"
        size="xl"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsPreviewOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                handleApply();
                setIsPreviewOpen(false);
              }}
            >
              Apply to Draft
            </Button>
          </>
        }
      >
        {previewDocument ? (
          <div className="max-h-[60vh] overflow-auto">
            <DashboardViewer document={previewDocument} />
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-zinc-500">
            No valid content to preview
          </p>
        )}
      </Modal>
    </div>
  );
}
